import { useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { findCountry, COUNTRIES } from "@/lib/countries";
import SignupFields, { emptySignupForm } from "@/components/SignupFields";
import {
  Users,
  UserPlus,
  Wallet,
  Plus,
  Minus,
  Key,
  Trash2,
  Pencil,
  Save,
  X,
  Activity,
  ShieldCheck,
  LayoutDashboard,
  PhoneCall,
  Mail,
  Copy,
  Search,
  CheckCircle2,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

const fmtEur = (n) =>
  `€${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "leads", label: "Leads", icon: PhoneCall },
  { key: "transactions", label: "Transactions", icon: Activity },
  { key: "wallet", label: "Settings", icon: Wallet },
];

export default function AdminDashboard() {
  const { user: adminUser, btcEur } = useAuth();
  const isOperator = adminUser?.role === "operator";
  const NAV_VISIBLE = useMemo(
    () => (isOperator ? NAV.filter((n) => n.key !== "wallet") : NAV),
    [isOperator]
  );
  const [section, setSection] = useState("overview");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState("");
  const [walletDraft, setWalletDraft] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [txs, setTxs] = useState([]);

  const load = async () => {
    try {
      const requests = [
        api.get("/admin/users"),
        api.get("/admin/stats"),
        api.get("/admin/transactions"),
      ];
      if (!isOperator) requests.push(api.get("/admin/wallet"));
      const [u, s, t, w] = await Promise.all(requests);
      setUsers(u.data);
      setStats(s.data);
      setTxs(t.data);
      if (w) {
        setWallet(w.data.address);
        setWalletDraft(w.data.address);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveWallet = async () => {
    try {
      await api.put("/admin/wallet", { address: walletDraft });
      setWallet(walletDraft);
      toast.success("Deposit wallet updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const leads = useMemo(
    () => users.filter((u) => u.role === "user"),
    [users]
  );

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6" data-testid="admin-dashboard">
        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-24 lg:self-start bg-[#0A0A0A] border border-white/10">
          <div className="p-4 border-b border-white/10">
            <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700]">
              / Console
            </p>
            <p className="text-sm text-white/60 mt-1 truncate">{adminUser?.email}</p>
          </div>
          <nav className="p-2 flex lg:flex-col gap-1 overflow-x-auto">
            {NAV_VISIBLE.map((n) => {
              const Icon = n.icon;
              const active = section === n.key;
              return (
                <button
                  key={n.key}
                  data-testid={`nav-${n.key}`}
                  onClick={() => setSection(n.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors whitespace-nowrap ${
                    active
                      ? "bg-[#FFD700] text-black font-semibold"
                      : "text-white/70 hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{n.label}</span>
                  {n.key === "leads" && leads.length > 0 && (
                    <span
                      className={`ml-auto text-xs px-1.5 py-0.5 font-mono ${
                        active ? "bg-black text-[#FFD700]" : "bg-white/10 text-white/70"
                      }`}
                    >
                      {leads.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* MAIN */}
        <main className="min-w-0">
          {section === "overview" && (
            <OverviewSection
              stats={stats}
              onAdd={() => setShowCreate(true)}
              txs={txs}
              btcEur={btcEur}
            />
          )}
          {section === "users" && (
            <UsersSection
              users={users}
              adminId={adminUser?.id}
              isOperator={isOperator}
              onChange={load}
              onAdd={() => setShowCreate(true)}
              btcEur={btcEur}
            />
          )}
          {section === "leads" && <LeadsSection leads={leads} btcEur={btcEur} />}
          {section === "transactions" && <TransactionsSection txs={txs} users={users} onChange={load} btcEur={btcEur} />}
          {section === "wallet" && !isOperator && (
            <WalletSection
              wallet={wallet}
              walletDraft={walletDraft}
              setWalletDraft={setWalletDraft}
              onSave={saveWallet}
            />
          )}
        </main>
      </div>

      {showCreate && (
        <CreateUserModal
          isOperator={isOperator}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- SECTIONS ---------------- */
function SectionHeader({ label, title, action }) {
  return (
    <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
          {label}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          {title}
        </h1>
      </div>
      {action}
    </div>
  );
}

function OverviewSection({ stats, onAdd, txs, btcEur }) {
  return (
    <div>
      <SectionHeader
        label="/ Admin Control"
        title="Operations Console"
        action={
          <button
            data-testid="open-create-user-btn"
            onClick={onAdd}
            className="bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold px-4 py-2.5 flex items-center gap-2 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Users" value={stats?.total_users ?? "—"} icon={<Users />} />
        <StatCard label="Admins" value={stats?.total_admins ?? "—"} icon={<ShieldCheck />} />
        <StatCard label="Transactions" value={stats?.total_transactions ?? "—"} icon={<Activity />} />
        <StatCard
          label="Total Balance"
          value={stats ? fmtEur((stats.total_balance_btc || 0) * (btcEur || 0)) : "—"}
          icon={<Wallet />}
          mono
        />
      </div>

      <div className="bg-[#0A0A0A] border border-white/10">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#FFD700]" />
          <p className="text-xs font-mono uppercase tracking-widest text-white/50">
            Latest Activity
          </p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {txs.length === 0 ? (
            <p className="px-6 py-10 text-white/40 text-sm text-center font-mono">
              No transactions yet.
            </p>
          ) : (
            txs.slice(0, 10).map((t) => (
              <div
                key={t.id}
                className="px-6 py-3 border-b border-white/5 flex justify-between items-center hover:bg-white/5 transition-colors"
              >
                <div className="text-xs">
                  <div className="text-white/60">{t.note}</div>
                  <div className="text-white/30 font-mono mt-1">
                    {new Date(t.created_at).toLocaleString()}
                  </div>
                </div>
                <div
                  className={`font-mono text-sm font-bold ${
                    t.amount_btc >= 0 ? "text-[#10B981]" : "text-[#F23F42]"
                  }`}
                >
                  {t.amount_btc >= 0 ? "+" : ""}
                  {fmtEur(t.amount_btc * (btcEur || 0))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function UsersSection({ users, adminId, onChange, onAdd, btcEur, isOperator = false }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [users]);

  const filtered = useMemo(() => {
    if (!q.trim()) return sorted;
    const s = q.toLowerCase().trim();
    const digits = s.replace(/[^0-9]/g, "");
    return sorted.filter((u) => {
      const emailMatch = (u.email || "").toLowerCase().includes(s);
      const nameMatch = (u.name || "").toLowerCase().includes(s) ||
                        (u.first_name || "").toLowerCase().includes(s) ||
                        (u.last_name || "").toLowerCase().includes(s);
      const rawPhone = `${u.dial_code || ""}${u.phone || ""}`.replace(/[^0-9]/g, "");
      const phoneMatch = (u.phone || "").toLowerCase().includes(s) ||
                         (digits && rawPhone.includes(digits));
      return emailMatch || nameMatch || phoneMatch;
    });
  }, [sorted, q]);

  useEffect(() => { setPage(1); }, [q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  return (
    <div>
      <SectionHeader
        label="/ User Management"
        title="All Accounts"
        action={
          <button
            data-testid="open-create-user-btn"
            onClick={onAdd}
            className="bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold px-4 py-2.5 flex items-center gap-2 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        }
      />
      <div className="mb-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          data-testid="users-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, name, or phone…"
          className="w-full bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none pl-9 pr-3 py-2.5 text-sm font-mono"
        />
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="text-left text-xs font-mono uppercase tracking-widest text-white/40 border-b border-white/10">
                <th className="px-6 py-3">User</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Balance</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === adminId}
                  isOperator={isOperator}
                  onChange={onChange}
                  btcEur={btcEur}
                />
              ))}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-white/40 text-sm font-mono">
                    {q ? "No users match your search." : "No users yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs font-mono text-white/50">
              Page {currentPage} of {totalPages} · {filtered.length} user{filtered.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-1">
              <button
                data-testid="users-page-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-white/20 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                .map((n, idx, arr) => (
                  <span key={n} className="flex items-center">
                    {idx > 0 && arr[idx - 1] !== n - 1 && (
                      <span className="text-white/30 font-mono px-1">…</span>
                    )}
                    <button
                      data-testid={`users-page-${n}`}
                      onClick={() => setPage(n)}
                      className={`min-w-8 h-8 px-2 font-mono text-xs transition-colors ${
                        n === currentPage
                          ? "bg-[#FFD700] text-black font-bold"
                          : "border border-white/20 hover:bg-white/10"
                      }`}
                    >
                      {n}
                    </button>
                  </span>
                ))}
              <button
                data-testid="users-page-next"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-white/20 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LeadsSection({ leads, btcEur }) {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("ALL");
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const sortedLeads = useMemo(() => {
    // Latest creations first
    return [...leads].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [leads]);

  const filtered = useMemo(() => {
    return sortedLeads.filter((l) => {
      if (country !== "ALL" && l.country !== country) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        (l.name || "").toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s) ||
        (l.phone || "").toLowerCase().includes(s) ||
        `${l.dial_code || ""}${l.phone || ""}`.includes(s)
      );
    });
  }, [sortedLeads, q, country]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [q, country]);

  const countriesInList = useMemo(() => {
    const set = new Set(leads.map((l) => l.country).filter(Boolean));
    return Array.from(set).sort();
  }, [leads]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div>
      <SectionHeader
        label="/ Leads"
        title={`Call Sheet · ${leads.length}`}
      />

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or phone…"
            className="w-full bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none pl-9 pr-3 py-2.5 text-sm font-mono"
          />
        </div>
        <select
          data-testid="leads-country-filter"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none px-3 py-2.5 text-sm font-mono"
        >
          <option value="ALL">All countries</option>
          {countriesInList.map((c) => {
            const meta = findCountry(c);
            return (
              <option key={c} value={c}>
                {meta.flag} {meta.name}
              </option>
            );
          })}
        </select>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead>
              <tr className="text-left text-xs font-mono uppercase tracking-widest text-white/40 border-b border-white/10">
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Contact</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((l) => {
                const meta = findCountry(l.country);
                const fullPhone = `${l.dial_code || ""}${l.phone || ""}`.replace(/\s+/g, "");
                return (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{l.name || l.email}</div>
                      <div className="text-xs text-white/40 font-mono">
                        Balance: {fmtEur((l.balance_btc || 0) * (btcEur || 0))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-lg leading-none mr-1">{meta.flag}</span>
                      <span className="text-white/70">{meta.name}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      {fullPhone ? (
                        <a
                          data-testid={`lead-call-${l.id}`}
                          href={`tel:${fullPhone}`}
                          className="text-[#FFD700] hover:underline"
                        >
                          {l.dial_code} {l.phone}
                        </a>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={`mailto:${l.email}`}
                        className="text-white/70 hover:text-white"
                      >
                        {l.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-xs text-white/50 font-mono">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        {fullPhone && (
                          <a
                            data-testid={`lead-call-btn-${l.id}`}
                            href={`tel:${fullPhone}`}
                            className="p-2 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 transition-colors"
                            title="Call"
                          >
                            <PhoneCall className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <a
                          href={`mailto:${l.email}`}
                          className="p-2 border border-white/20 hover:bg-white/10 transition-colors"
                          title="Email"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                        {fullPhone && (
                          <button
                            onClick={() => copy(fullPhone)}
                            className="p-2 border border-white/20 hover:bg-white/10 transition-colors"
                            title="Copy phone"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-white/40 text-sm font-mono">
                    No leads match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs font-mono text-white/50">
              Page {currentPage} of {totalPages} · {filtered.length} lead{filtered.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-1">
              <button
                data-testid="leads-page-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-white/20 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                .map((n, idx, arr) => (
                  <span key={n} className="flex items-center">
                    {idx > 0 && arr[idx - 1] !== n - 1 && (
                      <span className="text-white/30 font-mono px-1">…</span>
                    )}
                    <button
                      data-testid={`leads-page-${n}`}
                      onClick={() => setPage(n)}
                      className={`min-w-8 h-8 px-2 font-mono text-xs transition-colors ${
                        n === currentPage
                          ? "bg-[#FFD700] text-black font-bold"
                          : "border border-white/20 hover:bg-white/10"
                      }`}
                    >
                      {n}
                    </button>
                  </span>
                ))}
              <button
                data-testid="leads-page-next"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-white/20 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TransactionsSection({ txs, users, onChange, btcEur }) {
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const userMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const nameFor = (id) => {
    const u = userMap.get(id);
    return u ? u.name || u.email : id.slice(0, 8) + "…";
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return txs;
    const s = q.toLowerCase().trim();
    return txs.filter((t) => {
      const u = userMap.get(t.user_id);
      const name = ((u?.name || "") + " " + (u?.first_name || "") + " " + (u?.last_name || "")).toLowerCase();
      const email = (u?.email || t.user_email || "").toLowerCase();
      const note = (t.note || "").toLowerCase();
      const admin = (t.admin_email || "").toLowerCase();
      return (
        name.includes(s) ||
        email.includes(s) ||
        note.includes(s) ||
        admin.includes(s)
      );
    });
  }, [txs, q, userMap]);

  useEffect(() => { setPage(1); }, [q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const del = async (id) => {
    if (!window.confirm("Delete this transaction? It will also disappear from the user's log.")) return;
    try {
      await api.delete(`/admin/transactions/${id}`);
      toast.success("Transaction deleted");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <SectionHeader label="/ Ledger" title="All Transactions" />
      <div className="mb-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          data-testid="transactions-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by user name, email, or transaction note…"
          className="w-full bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none pl-9 pr-3 py-2.5 text-sm font-mono"
        />
      </div>
      <div className="bg-[#0A0A0A] border border-white/10">
        <div>
          {paged.length === 0 ? (
            <p className="px-6 py-10 text-white/40 text-sm text-center font-mono">
              {q ? "No transactions match your search." : "No transactions yet."}
            </p>
          ) : (
            paged.map((t) => (
              <div key={t.id}>
                <div
                  data-testid={`admin-tx-row-${t.id}`}
                  className="px-6 py-3 border-b border-white/5 flex justify-between items-center hover:bg-white/5 transition-colors gap-4"
                >
                  <div className="text-xs flex-1 min-w-0">
                    <div className="text-sm text-white/80">{nameFor(t.user_id)}</div>
                    <div className="text-white/40 mt-1">{t.note}</div>
                    <div className="text-white/30 font-mono mt-1">
                      {new Date(t.created_at).toLocaleString()} · by {t.admin_email}
                    </div>
                  </div>
                  <div
                    className={`font-mono text-sm font-bold ${
                      t.amount_btc >= 0 ? "text-[#10B981]" : "text-[#F23F42]"
                    }`}
                  >
                    {t.amount_btc >= 0 ? "+" : ""}
                    {fmtEur(t.amount_btc * (btcEur || 0))}
                  </div>
                  <button
                    data-testid={`edit-tx-btn-${t.id}`}
                    onClick={() => setEditingId(editingId === t.id ? null : t.id)}
                    className="p-2 border border-white/20 text-white/70 hover:bg-white/10 transition-colors"
                    title="Edit transaction"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`delete-tx-btn-${t.id}`}
                    onClick={() => del(t.id)}
                    className="p-2 border border-[#F23F42]/40 text-[#F23F42] hover:bg-[#F23F42]/10 transition-colors"
                    title="Delete transaction"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {editingId === t.id && (
                  <TxEditor
                    tx={t}
                    onDone={() => {
                      setEditingId(null);
                      onChange();
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                )}
              </div>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs font-mono text-white/50">
              Page {currentPage} of {totalPages} · {filtered.length} transaction{filtered.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-1">
              <button
                data-testid="transactions-page-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-white/20 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                .map((n, idx, arr) => (
                  <span key={n} className="flex items-center">
                    {idx > 0 && arr[idx - 1] !== n - 1 && (
                      <span className="text-white/30 font-mono px-1">…</span>
                    )}
                    <button
                      data-testid={`transactions-page-${n}`}
                      onClick={() => setPage(n)}
                      className={`min-w-8 h-8 px-2 font-mono text-xs transition-colors ${
                        n === currentPage
                          ? "bg-[#FFD700] text-black font-bold"
                          : "border border-white/20 hover:bg-white/10"
                      }`}
                    >
                      {n}
                    </button>
                  </span>
                ))}
              <button
                data-testid="transactions-page-next"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-white/20 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TxEditor({ tx, onDone, onCancel }) {
  // Convert ISO to datetime-local ("YYYY-MM-DDTHH:MM")
  const toLocalInput = (iso) => {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };
  const [note, setNote] = useState(tx.note || "");
  const [when, setWhen] = useState(toLocalInput(tx.created_at));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (note !== tx.note) body.note = note;
      if (when && when !== toLocalInput(tx.created_at)) {
        // Convert to ISO with local timezone
        body.created_at = new Date(when).toISOString();
      }
      if (Object.keys(body).length === 0) {
        toast.info("No changes");
        onDone();
        return;
      }
      await api.patch(`/admin/transactions/${tx.id}`, body);
      toast.success("Transaction updated");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-black border-b border-white/10 px-6 py-4 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[220px]">
        <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
          Date &amp; time
        </label>
        <input
          data-testid={`edit-tx-when-${tx.id}`}
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2 font-mono text-sm"
        />
      </div>
      <div className="flex-1 min-w-[220px]">
        <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
          Note
        </label>
        <input
          data-testid={`edit-tx-note-${tx.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2 text-sm"
          placeholder="Crypto recovered successfully"
        />
      </div>
      <button
        data-testid={`save-tx-btn-${tx.id}`}
        onClick={save}
        disabled={saving}
        className="px-4 py-2 bg-[#FFD700] text-black font-semibold hover:bg-[#F4C430] disabled:opacity-50 transition-colors flex items-center gap-1"
      >
        <Save className="w-4 h-4" /> Save
      </button>
      <button
        onClick={onCancel}
        className="px-3 py-2 border border-white/20 hover:bg-white/10 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function WalletSection({ wallet, walletDraft, setWalletDraft, onSave }) {
  const [waNumber, setWaNumber] = useState("");
  const [waDraft, setWaDraft] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/whatsapp");
        setWaNumber(data.number || "");
        setWaDraft(data.number || "");
      } catch { /* ignore */ }
    })();
  }, []);

  const saveWa = async () => {
    try {
      await api.put("/admin/whatsapp", { number: waDraft });
      setWaNumber(waDraft);
      toast.success("WhatsApp number updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <SectionHeader label="/ Wallet" title="Company Deposit Address" />
      <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-2xl mb-6">
        <p className="text-sm text-white/60 mb-4">
          This BTC address is shown to every client on their dashboard as the deposit
          destination. Update carefully.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="admin-wallet-input"
            value={walletDraft}
            onChange={(e) => setWalletDraft(e.target.value)}
            className="flex-1 bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 font-mono text-sm"
            placeholder="Enter BTC address"
          />
          <button
            data-testid="save-wallet-btn"
            onClick={onSave}
            disabled={walletDraft === wallet}
            className="px-5 py-2.5 bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-4 h-4 text-[#25D366]" />
          <p className="text-xs font-mono uppercase tracking-widest text-white/50">
            WhatsApp Support Number
          </p>
        </div>
        <p className="text-sm text-white/60 mb-4">
          Shown as a floating button on every client dashboard. Include the country
          code (e.g. +44 for UK).
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="admin-whatsapp-input"
            value={waDraft}
            onChange={(e) => setWaDraft(e.target.value)}
            className="flex-1 bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 font-mono text-sm"
            placeholder="+44 20 7946 0000"
          />
          <button
            data-testid="save-whatsapp-btn"
            onClick={saveWa}
            disabled={waDraft === waNumber || !waDraft.trim()}
            className="px-5 py-2.5 bg-[#25D366] hover:bg-[#1EBE5C] text-white font-semibold disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- ROW / MODAL ---------------- */
function StatCard({ label, value, icon, mono }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-white/50">
          {label}
        </span>
        <span className="text-[#FFD700]">{icon}</span>
      </div>
      <div className={`text-2xl font-bold ${mono ? "font-mono" : "font-display tracking-tighter"}`}>
        {value}
      </div>
    </div>
  );
}

function UserRow({ user, isSelf, onChange, btcEur, isOperator = false }) {
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(user.first_name || "");
  const [last, setLast] = useState(user.last_name || "");
  const [role, setRole] = useState(user.role);
  const [showBalance, setShowBalance] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showTax, setShowTax] = useState(false);
  const isAdmin = user.role === "admin";

  const toggleVerify = async () => {
    try {
      await api.post(`/admin/users/${user.id}/verify`, { verified: !user.verified });
      toast.success(
        !user.verified
          ? `${user.email} is now VERIFIED`
          : `${user.email} is now UNVERIFIED`
      );
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const meta = findCountry(user.country);

  const save = async () => {
    try {
      await api.patch(`/admin/users/${user.id}`, {
        name: `${first} ${last}`.trim(),
        role,
      });
      toast.success("User updated");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${user.email}?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("User deleted");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`user-row-${user.id}`}>
        <td className="px-6 py-4">
          {editing ? (
            <div className="flex gap-2">
              <input
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                placeholder="First"
                className="bg-transparent border border-white/20 px-2 py-1 text-sm font-mono w-24"
              />
              <input
                value={last}
                onChange={(e) => setLast(e.target.value)}
                placeholder="Last"
                className="bg-transparent border border-white/20 px-2 py-1 text-sm font-mono w-24"
              />
            </div>
          ) : (
            <>
              <div className="text-sm flex items-center gap-2">
                <span>{user.name || user.email}</span>
                {user.country && <span className="text-base leading-none">{meta.flag}</span>}
              </div>
              <div className="text-xs text-white/40 font-mono">{user.email}</div>
              {user.phone && (
                <div className="text-xs text-white/40 font-mono">
                  {user.dial_code} {user.phone}
                </div>
              )}
            </>
          )}
        </td>
        <td className="px-6 py-4">
          {editing && !isOperator ? (
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="bg-black border border-white/20 px-2 py-1 text-sm font-mono"
            >
              <option value="user">user</option>
              <option value="operator">operator</option>
              <option value="admin">admin</option>
            </select>
          ) : (
            <span
              className={`text-xs font-mono uppercase tracking-widest px-2 py-1 border ${
                user.role === "admin"
                  ? "border-[#FFD700] text-[#FFD700]"
                  : user.role === "operator"
                  ? "border-[#7DD3FC] text-[#7DD3FC]"
                  : "border-white/20 text-white/60"
              }`}
            >
              {user.role}
            </span>
          )}
        </td>
        <td className="px-6 py-4 font-mono text-sm">
          {fmtEur((user.balance_btc || 0) * (btcEur || 0))}
          {user.tax_percentage > 0 && (
            <div className="text-xs font-mono text-[#FFD700] mt-1">
              Tax: {user.tax_percentage}%
            </div>
          )}
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <button
              data-testid={`adjust-balance-btn-${user.id}`}
              onClick={() => setShowBalance(true)}
              className="p-2 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 transition-colors"
              title="Adjust balance"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid={`change-pwd-btn-${user.id}`}
              onClick={() => setShowPwd(true)}
              className="p-2 border border-white/20 hover:bg-white/10 transition-colors"
              title="Change password"
            >
              <Key className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid={`tax-btn-${user.id}`}
              onClick={() => setShowTax(true)}
              className="p-2 border border-white/20 hover:bg-white/10 transition-colors text-xs px-3 font-mono"
              title="Set tax %"
            >
              Tax %
            </button>
            {!isAdmin && (
              <button
                data-testid={`verify-btn-${user.id}`}
                onClick={toggleVerify}
                className={`p-2 border transition-colors text-xs px-3 font-mono uppercase tracking-widest flex items-center gap-1.5 ${
                  user.verified
                    ? "border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                    : "border-[#F23F42] text-[#F23F42] hover:bg-[#F23F42]/10"
                }`}
                title={user.verified ? "Unverify" : "Verify"}
              >
                {user.verified ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <ShieldAlert className="w-3.5 h-3.5" />
                )}
                {user.verified ? "Verified" : "Unverified"}
              </button>
            )}
            {editing ? (
              <button
                onClick={save}
                data-testid={`save-user-btn-${user.id}`}
                className="p-2 border border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 transition-colors"
              >
                <Save className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                data-testid={`edit-user-btn-${user.id}`}
                onClick={() => setEditing(true)}
                className="p-2 border border-white/20 hover:bg-white/10 transition-colors text-xs px-3"
              >
                Edit
              </button>
            )}
            {!isSelf && (
              <button
                data-testid={`delete-user-btn-${user.id}`}
                onClick={del}
                className="p-2 border border-[#F23F42]/40 text-[#F23F42] hover:bg-[#F23F42]/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {showBalance && (
        <tr>
          <td colSpan={4} className="px-0 py-0">
            <BalanceAdjuster
              user={user}
              onDone={() => {
                setShowBalance(false);
                onChange();
              }}
              onCancel={() => setShowBalance(false)}
            />
          </td>
        </tr>
      )}
      {showPwd && (
        <tr>
          <td colSpan={4} className="px-0 py-0">
            <PasswordChanger
              user={user}
              onDone={() => setShowPwd(false)}
              onCancel={() => setShowPwd(false)}
            />
          </td>
        </tr>
      )}
      {showTax && (
        <tr>
          <td colSpan={4} className="px-0 py-0">
            <TaxSetter
              user={user}
              onDone={() => {
                setShowTax(false);
                onChange();
              }}
              onCancel={() => setShowTax(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function BalanceAdjuster({ user, onDone, onCancel }) {
  const { btcEur } = useAuth();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(false);

  const btcVal = parseFloat(amount);
  const eurPreview =
    !Number.isNaN(btcVal) && btcVal > 0 && btcEur > 0 ? btcVal * btcEur : 0;

  const submit = async (sign) => {
    if (Number.isNaN(btcVal) || btcVal <= 0) {
      toast.error("Enter a positive BTC amount");
      return;
    }
    try {
      const res = await api.post(`/admin/users/${user.id}/balance`, {
        amount_btc: sign * btcVal,
        note: note || undefined,
        send_email: sendEmail,
      });
      toast.success(
        `${sign > 0 ? "Added" : "Removed"} ₿${btcVal.toFixed(8)} ${sign > 0 ? "to" : "from"} ${user.email}'s balance${res.data.email_sent ? " · email sent" : ""}`
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-black border-b border-white/10 px-6 py-4 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[180px]">
        <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
          Amount (BTC)
        </label>
        <input
          data-testid={`balance-amount-input-${user.id}`}
          type="number"
          step="0.00000001"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2 font-mono text-sm"
          placeholder="0.01"
        />
        {eurPreview > 0 && (
          <div className="text-[10px] font-mono text-white/40 mt-1">
            ≈ {fmtEur(eurPreview)}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[180px]">
        <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
          Note (optional)
        </label>
        <input
          data-testid={`balance-note-input-${user.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2 text-sm"
          placeholder="Crypto recovered successfully"
        />
      </div>
      <label
        className="flex items-center gap-2 text-xs font-mono text-white/70 cursor-pointer select-none pb-2"
        data-testid={`send-email-toggle-${user.id}`}
      >
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="accent-[#FFD700] w-4 h-4"
        />
        Also send email
      </label>
      <button
        data-testid={`credit-btn-${user.id}`}
        onClick={() => submit(1)}
        className="px-4 py-2 bg-[#10B981] text-black font-semibold hover:bg-[#10B981]/90 transition-colors flex items-center gap-1"
      >
        <Plus className="w-4 h-4" /> Add
      </button>
      <button
        data-testid={`debit-btn-${user.id}`}
        onClick={() => submit(-1)}
        className="px-4 py-2 border border-[#F23F42] text-[#F23F42] hover:bg-[#F23F42]/10 transition-colors flex items-center gap-1"
      >
        <Minus className="w-4 h-4" /> Subtract
      </button>
      <button
        onClick={onCancel}
        className="px-3 py-2 border border-white/20 hover:bg-white/10 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function PasswordChanger({ user, onDone, onCancel }) {
  const [pwd, setPwd] = useState("");
  const submit = async () => {
    if (pwd.length < 4) {
      toast.error("Password must be at least 4 chars");
      return;
    }
    try {
      await api.post(`/admin/users/${user.id}/password`, { new_password: pwd });
      toast.success(`Password updated for ${user.email}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="bg-black border-b border-white/10 px-6 py-4 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[240px]">
        <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
          New password for {user.email}
        </label>
        <input
          data-testid={`new-pwd-input-${user.id}`}
          type="text"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2 font-mono text-sm"
        />
      </div>
      <button
        data-testid={`save-pwd-btn-${user.id}`}
        onClick={submit}
        className="px-4 py-2 bg-[#FFD700] text-black font-semibold hover:bg-[#F4C430] transition-colors flex items-center gap-1"
      >
        <Save className="w-4 h-4" /> Update
      </button>
      <button
        onClick={onCancel}
        className="px-3 py-2 border border-white/20 hover:bg-white/10 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function TaxSetter({ user, onDone, onCancel }) {
  const [pct, setPct] = useState(String(user.tax_percentage || 0));
  const submit = async () => {
    const val = parseFloat(pct);
    if (Number.isNaN(val) || val < 0 || val > 100) {
      toast.error("Enter a percentage between 0 and 100");
      return;
    }
    try {
      await api.post(`/admin/users/${user.id}/tax`, { tax_percentage: val });
      toast.success(
        val === 0
          ? `Tax removed for ${user.email}`
          : `Tax set to ${val}% for ${user.email}`
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="bg-black border-b border-white/10 px-6 py-4 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[240px]">
        <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
          Tax percentage for {user.email} (0–100, 0 removes it)
        </label>
        <input
          data-testid={`tax-input-${user.id}`}
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2 font-mono text-sm"
        />
      </div>
      <button
        data-testid={`save-tax-btn-${user.id}`}
        onClick={submit}
        className="px-4 py-2 bg-[#FFD700] text-black font-semibold hover:bg-[#F4C430] transition-colors flex items-center gap-1"
      >
        <Save className="w-4 h-4" /> Save
      </button>
      <button
        onClick={onCancel}
        className="px-3 py-2 border border-white/20 hover:bg-white/10 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated, isOperator = false }) {
  const { btcEur } = useAuth();
  const [form, setForm] = useState({
    ...emptySignupForm(),
    role: "user",
    balance_btc: 0,
  });
  const btcVal = parseFloat(form.balance_btc) || 0;
  const eurPreview = btcVal > 0 && btcEur > 0 ? btcVal * btcEur : 0;
  const isStandardUser = form.role === "user";
  const submit = async (e) => {
    e.preventDefault();
    try {
      // Staff accounts (admin/operator) don't need the client-shaped fields
      // (phone/country/balance), so only send what's relevant.
      const base = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        role: form.role,
      };
      const payload = isStandardUser
        ? {
            ...base,
            phone: form.phone,
            dial_code: form.dial_code,
            country: form.country,
            balance_btc: btcVal,
          }
        : base;
      await api.post("/admin/users", payload);
      toast.success(`Created ${form.role}: ${form.email}`);
      onCreated();
    } catch (e2) {
      toast.error(formatApiError(e2));
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-md w-full p-8 relative my-auto">
        <button
          onClick={onClose}
          data-testid="close-create-modal-btn"
          className="absolute top-4 right-4 p-1 hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
          / New Account
        </p>
        <h2 className="font-display text-3xl font-black tracking-tighter mb-6">
          Create {isStandardUser ? "User" : form.role === "admin" ? "Admin" : "Operator"}
        </h2>
        <form onSubmit={submit} className="space-y-4">
          {/* Role selector first — toggles field set below */}
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
              Role
            </label>
            {isOperator ? (
              <div
                data-testid="create-role-locked"
                className="w-full bg-black border border-white/10 px-3 py-2.5 font-mono text-sm text-white/60"
                title="Operators can only create standard users"
              >
                user
              </div>
            ) : (
              <select
                data-testid="create-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-black border border-white/20 focus:border-white outline-none px-3 py-2.5 font-mono text-sm"
              >
                <option value="user">user</option>
                <option value="operator">operator</option>
                <option value="admin">admin</option>
              </select>
            )}
          </div>

          {isStandardUser ? (
            <>
              <SignupFields form={form} setForm={setForm} prefix="create" stacked />
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-1">
                  Initial Balance (BTC)
                </label>
                <input
                  data-testid="create-balance"
                  type="number"
                  step="0.00000001"
                  value={form.balance_btc}
                  onChange={(e) => setForm({ ...form, balance_btc: e.target.value })}
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 font-mono text-sm"
                />
                {eurPreview > 0 && (
                  <div className="text-[10px] font-mono text-white/40 mt-1">
                    ≈ {fmtEur(eurPreview)}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StaffField
                  label="First name"
                  value={form.first_name}
                  onChange={(v) => setForm({ ...form, first_name: v })}
                  testid="create-first-name-staff"
                />
                <StaffField
                  label="Last name"
                  value={form.last_name}
                  onChange={(v) => setForm({ ...form, last_name: v })}
                  testid="create-last-name-staff"
                />
              </div>
              <StaffField
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testid="create-email-staff"
              />
              <StaffField
                label="Password"
                type="password"
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                placeholder="Min. 4"
                testid="create-password-staff"
              />
            </>
          )}

          <button
            type="submit"
            data-testid="create-user-submit-btn"
            className="w-full bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold py-3 transition-colors"
          >
            Create Account
          </button>
        </form>
      </div>
    </div>
  );
}

function StaffField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
        {label}
      </span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border border-white/20 focus:border-white focus:ring-1 focus:ring-white outline-none px-3 py-3 text-sm font-mono placeholder:text-white/30"
      />
    </label>
  );
}
